'use client';

import { Link } from 'src/navigation';
import styles from './breadcrumbs.module.scss';
import HomeIcon from 'assets/icons/home.svg';
import { usePathname } from 'next/navigation';
import { makeSegments } from 'lib/makeSegments';
import MarkdownParser from 'components/rich-text/markdown-parser';
import type { BreadcrumbSegment } from '@metmuseum/types';

const Breadcrumbs = ({
	segments,
	lastSegmentOverride,
	omitSegmentsAt = [],
}: Props) => {
	const pathname = usePathname();
	const urlSegments = [...(segments || makeSegments(pathname))] // make a copy so we don't mutate the original
		.filter((_segment, index) => !omitSegmentsAt.includes(index)); // remove segments to be omitted

	if (!urlSegments?.length) {
		return null;
	}

	const lastItem = urlSegments.pop();
	const lastCrumb = lastSegmentOverride || lastItem.name;

	return (
		<nav className={styles.breadcrumbs} aria-label="breadcrumbs">
			<span key={'home'} className={styles.linkWrapper}>
				<Link aria-label="home" className={styles.link} href="/">
					<HomeIcon className={styles.svg} />
				</Link>
			</span>
			{urlSegments.map((segment: BreadcrumbSegment) => {
				return (
					<span key={segment.path} className={styles.linkWrapper}>
						<Link
							title={segment.name}
							aria-label={segment.name}
							className={styles.link}
							href={segment.path}
						>
							<MarkdownParser>{segment.name}</MarkdownParser>
						</Link>
					</span>
				);
			})}
			<span key={lastItem.path} className={styles.lastItem}>
				<span className={styles.truncatedSegment} title={lastCrumb}>
					<MarkdownParser>{lastCrumb}</MarkdownParser>
				</span>
			</span>
		</nav>
	);
};

export type Props =
	| {
			segments?: BreadcrumbSegment[];
			lastSegmentOverride?: never; // intented to only be used when the rest of the breadcrumbs are automatically generated
			/** @remarks useful for skipping a segment in the middle that we don't have a page for */
			omitSegmentsAt?: number[];
	  }
	| {
			segments?: never;
			lastSegmentOverride?: string;
			omitSegmentsAt?: never;
	  };

export default Breadcrumbs;
